import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { api, getUser, logout } from "@/lib/api";
import { LogOut, AlertTriangle, Menu, X, BadgeCheck, Minimize2, Maximize2 } from "lucide-react";
import RadioPlayer from "@/components/RadioPlayer";
import DemoBanner from "@/components/DemoBanner";
import PrisonerBadge from "@/components/PrisonerBadge";

const API = process.env.REACT_APP_BACKEND_URL;  // iter 31.321 — /handoffs/stats poll

// Iter 31.76 — render the operator's avatar. Preference order:
//   1. Google profile picture (set on /oauth/google/exchange)
//   2. Legacy LinkedIn picture (kept on existing user rows from iter < 30)
//   3. Prisoner-numbered badge derived from user.id
// If the remote picture fails to load (Google URL expires, CSP blocks it,
// referrer issues), fall through silently to the badge so the operator
// is never staring at a broken image icon.
function AvatarSlot({ user }) {
    const remote = user.google_picture || user.linkedin_picture;
    const [failed, setFailed] = React.useState(false);
    if (remote && !failed) {
        return (
            <img
                src={remote}
                alt={user.name || "Operative"}
                referrerPolicy="no-referrer"
                className="w-9 h-9 border border-ink/40 object-cover sf-glow"
                data-testid="current-user-avatar"
                title={user.name}
                onError={() => setFailed(true)}
            />
        );
    }
    return (
        <span
            className="w-9 h-9 border border-ink/40 sf-glow overflow-hidden"
            data-testid="current-user-avatar"
            title={user.name}
        >
            <PrisonerBadge user={user} size={36} title={user.name} />
        </span>
    );
}

const NAV = [
    { to: "/shredder", label: "Shredder" },
    { to: "/ledger", label: "Ledger" },
    { to: "/brief", label: "Brief" },
    { to: "/cv", label: "Résumé" },
    { to: "/comms", label: "Comms" },
    { to: "/cost", label: "Cost" },
    { to: "/patterns", label: "Patterns" },
    { to: "/callouts", label: "Callouts" },
    { to: "/team", label: "Team" },
    { to: "/spy-finance", label: "Spy Finance" },
    { to: "/spy-health", label: "Spy Health" },
    { to: "/target-sight", label: "Target Sight" },
    { to: "/billing", label: "Billing" },
    { to: "/devices", label: "Devices" },
    { to: "/phone-sentinel", label: "Phone Sentinel" },
    { to: "/linkedin", label: "LinkedIn" },
    { to: "/contacts", label: "Contacts" },
    { to: "/gdpr", label: "GDPR" },
    { to: "/incidents", label: "Incidents" },
    { to: "/vault", label: "Vault" },
    { to: "/dossiers", label: "Dossiers" },
    { to: "/radio", label: "Media" },
    { to: "/manual", label: "Manual" },
    { to: "/gmail-watch", label: "Gmail Watch" },
    { to: "/admin", label: "Admin" },
];

// Iter 31.208 — internal-only items. Surfaced under a dedicated "Internal"
// drawer section so Six can find the hidden surfaces without remembering
// every URL ("my memory is worse than Two's"). Non-internal roles never
// see this section.
const INTERNAL_ONLY_NAV = [
    { to: "/chamber",          label: "Chamber" },
    { to: "/agent/dossier/tv", label: "Dossier TV" },
    { to: "/agents",           label: "Agent Foundry" },
    { to: "/chat/gemini",      label: "Gemini Chat" },  // iter 31.313
    // Iter 31.321 — Handoff Inbox. Label carries a live pending count
    // badge injected at render time via `pendingHandoffs` state below.
    { to: "/handoffs",         label: "Handoffs" },
    { to: "/mail",             label: "Mail" },
    { to: "/roster",           label: "Roster" },
    { to: "/admin/roster",     label: "Roster Admin" },
    { to: "/diagnostics",      label: "Diagnostics" },
    { to: "/academy",          label: "Academy" },
    { to: "/pig-pen",          label: "Pig Pen" },
    { to: "/shop",             label: "Shop" },
    { to: "/upgrade",          label: "Upgrade" },
];

// Iter 31.7 (21 May 2026) — sectioned drawer + trimmed primary tabs.
// Per No. 6: the flat A→Z drawer of 24 items was too much. Six themed
// sections; the desktop folder-tabs keep only the daily-use slots.
// Academy / Shop / Pig Pen stubs retired in the same pass.
const NAV_SECTIONS = [
    { title: "Ops",         paths: ["/shredder", "/brief", "/ledger", "/radio", "/patterns", "/gmail-watch"] },
    { title: "Personnel",   paths: ["/team", "/contacts", "/callouts", "/users"] },
    { title: "Finance",     paths: ["/spy-finance", "/dossiers", "/cost", "/billing"] },
    { title: "Sensors",     paths: ["/spy-health", "/target-sight", "/phone-sentinel"] },
    { title: "Composition", paths: ["/cv", "/comms", "/linkedin"] },
    { title: "Vault",       paths: ["/vault", "/devices", "/manual", "/incidents"] },
    { title: "Admin",       paths: ["/admin"] },
    // Iter 31.208 — internal-only surfaces (Chamber, Dossier TV, Agent
    // Foundry, Mail, Roster, Diagnostics, etc).  Section appears only
    // when the current user.role === "internal".
    { title: "Internal",    paths: INTERNAL_ONLY_NAV.map((n) => n.to) },
];
// Desktop folder tabs — just the everyday surfaces. Everything else lives
// in the hamburger drawer's sections.
const PRIMARY_TABS = ["/shredder", "/brief", "/ledger", "/radio", "/spy-finance"];

export default function Shell({ children }) {
    const user = getUser();
    const loc = useLocation();
    const nav = useNavigate();
    const [budgetWarn, setBudgetWarn] = useState("");
    const [navOpen, setNavOpen] = useState(false);
    // Iter 31.197 — compact-header mode for 65" TV / low-DPI big-screen
    // browsers where the letterhead eats 16:9 vertical real-estate and
    // the operator can't zoom. Sticky once toggled. Tap the marker on
    // the slim bar to re-expand.
    const [headerCompact, setHeaderCompact] = useState(() => {
        try { return localStorage.getItem("shed_header_compact") === "1"; }
        catch { return false; }
    });
    const toggleHeaderCompact = () => {
        setHeaderCompact((v) => {
            const next = !v;
            try { localStorage.setItem("shed_header_compact", next ? "1" : "0"); } catch { /* ignore */ }
            return next;
        });
    };
    const [tier, setTier] = useState(null); // {tier, remaining_days} | null
    // Iter 31.49 — live credit balance pill. Refreshed on Shell mount
    // and on a custom `shed:credits-changed` event that endpoints can
    // dispatch after a debit/grant so the pill stays in sync without
    // polling.
    const [credits, setCredits] = useState(null); // {balance: int} | null
    // Iter 29: one-shot greeting after LinkedIn sign-in. Read the flag once
    // on mount, render the welcome card, clear the flag. Falsy after first
    // render so refreshes don't keep re-greeting.
    const [greeting, setGreeting] = useState(null);
    useEffect(() => {
        try {
            const raw = localStorage.getItem("shed_greeting");
            if (!raw) return;
            const g = JSON.parse(raw);
            // Discard if the greeting is older than 30s — protects against a stale
            // flag left behind by a sign-in attempt that didn't reach the dashboard.
            if (g && g.at && (Date.now() - g.at) < 30_000) {
                setGreeting(g);
            }
            localStorage.removeItem("shed_greeting");
        } catch (_) { /* non-fatal */ }
    }, []);

    useEffect(() => {
        const h = (e) => setBudgetWarn(e.detail || "Universal Key budget exceeded — top up to continue.");
        window.addEventListener("shed:budget-low", h);
        return () => window.removeEventListener("shed:budget-low", h);
    }, []);

    // Soft tier display — fetched once per Shell mount. Best-effort; if it
    // fails (no auth, network), the badge just doesn't render. Iter 19.
    // Iter 31.154 — depend on the stable user id (primitive) NOT the user
    // object. `getUser()` returns a fresh `JSON.parse(...)` reference on
    // every render, so `[user]` was firing this effect on every render →
    // setState → re-render → re-fire. Caused a poll storm on production
    // (337 req / 49s of alternating entitlements + balance). The id is
    // a string, compared by value, so the effect now fires exactly once
    // per signed-in identity.
    const userId = user?.id || null;
    useEffect(() => {
        if (!userId) return;
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.get("/payments/me/entitlements");
                if (cancelled) return;
                const active = data?.active?.[0];
                setTier(active ? { tier: active.tier, remaining_days: active.remaining_days } : { tier: "free" });
            } catch (_) {
                // ignore — badge stays hidden
            }
        })();
        return () => { cancelled = true; };
    }, [userId]);

    // Iter 31.49 — credit balance fetch + event-driven refresh.
    // Iter 31.154 — same stable-primitive dep fix as the tier effect above.
    useEffect(() => {
        if (!userId) return;
        let cancelled = false;
        const load = async () => {
            try {
                const { data } = await api.get("/credits/balance");
                if (!cancelled) setCredits({ balance: data?.balance ?? 0 });
            } catch (_) { /* ignore — pill stays hidden */ }
        };
        load();
        const handler = () => load();
        window.addEventListener("shed:credits-changed", handler);
        return () => {
            cancelled = true;
            window.removeEventListener("shed:credits-changed", handler);
        };
    }, [userId]);

    // Close mobile drawer on route change
    useEffect(() => { setNavOpen(false); }, [loc.pathname]);

    // Iter 31.321 — pending handoff count for the nav badge. Polls
    // every 60s while mounted. Cheap: /handoffs/stats is two
    // count_documents against a small index. Fails silently — a
    // failed poll just leaves the last known count in place.
    const [pendingHandoffs, setPendingHandoffs] = useState(0);
    useEffect(() => {
        if (!user || user.role !== "internal") return undefined;
        let cancelled = false;
        const fetchStats = async () => {
            try {
                const token = localStorage.getItem("shed_jwt");
                if (!token) return;
                const res = await fetch(`${API}/api/handoffs/stats`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                if (!res.ok) return;
                const data = await res.json();
                if (!cancelled) setPendingHandoffs(data?.pending || 0);
            } catch { /* silent */ }
        };
        fetchStats();
        const t = setInterval(fetchStats, 60000);
        return () => { cancelled = true; clearInterval(t); };
    }, [user]);

    const navItems = [...NAV];
    if (user?.role === "internal") {
        navItems.push({ to: "/users", label: "Operatives" });
        // Iter 31.208 — internal-only surfaces.
        for (const item of INTERNAL_ONLY_NAV) navItems.push(item);
    }
    // Per No. 6 (Feb 2026 / iter 31.7): primary folder tabs are a tiny
    // shortlist; the rest lives in the grouped drawer below. Shredder
    // pinned first across both surfaces.
    const byPath = Object.fromEntries(navItems.map((n) => [n.to, n]));
    const primaryNavItems = PRIMARY_TABS
        .map((p) => byPath[p])
        .filter(Boolean);
    // Drawer sections — drop any path that isn't in navItems (e.g.
    // /users when role !== internal). Drop a section if it ends up empty.
    // Iter 31.71 (26 May 2026, per Six) — restore A→Z alphabetisation
    // *inside* each section. Section order itself is editorial and stays
    // as listed in NAV_SECTIONS, but the items within a section read
    // alphabetically by label so nothing feels arbitrary.
    const drawerSections = NAV_SECTIONS
        .map((s) => ({
            title: s.title,
            items: s.paths
                .map((p) => byPath[p])
                .filter(Boolean)
                .slice()
                .sort((a, b) => a.label.localeCompare(b.label)),
        }))
        .filter((s) => s.items.length > 0);
    const activeItem = navItems.find(
        (n) => loc.pathname === n.to || loc.pathname.startsWith(n.to + "/")
    ) || navItems[0];

    return (
        <div className="min-h-screen text-ink" data-testid="app-shell">
            {greeting && (
                <div
                    className="bg-paper-light border-b border-ink/25 py-3 px-4 flex items-center justify-between gap-4 relative"
                    data-testid="linkedin-greeting-banner"
                >
                    <div className="absolute inset-0 pointer-events-none grain opacity-50" />
                    <div className="flex items-center gap-3 relative">
                        {greeting.picture && (
                            <img
                                src={greeting.picture}
                                alt={greeting.name || "Operative"}
                                referrerPolicy="no-referrer"
                                className="w-12 h-12 border border-ink/40 object-cover sf-glow"
                                data-testid="linkedin-greeting-avatar"
                                onError={(e) => { e.currentTarget.style.display = "none"; }}
                            />
                        )}
                        <div className="leading-tight">
                            <div className="label-typed">
                                {greeting.is_new ? "New file opened · Welcome to the dossier" : "Re-entry logged"}
                            </div>
                            <div
                                className="font-heading text-base md:text-lg text-ink tracking-wider"
                                data-testid="linkedin-greeting-name"
                            >
                                {greeting.is_new ? "Be seeing you, " : "Back through the door, "}
                                {greeting.name || "Operative"}.
                            </div>
                            {greeting.is_new && (
                                <div className="font-body text-[11px] text-ink-soft mt-0.5">
                                    Filed via LinkedIn · Bridge of Continuity · — No. 2
                                </div>
                            )}
                        </div>
                    </div>
                    <button
                        onClick={() => setGreeting(null)}
                        className="text-ink-soft hover:text-stamp-red relative"
                        data-testid="linkedin-greeting-dismiss"
                        title="Dismiss"
                        aria-label="Dismiss greeting"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>
            )}
            {budgetWarn && (
                <div
                    className="bg-stamp-red text-paper-light py-2 px-4 flex items-center justify-between gap-3 font-body text-xs tracking-[0.15em] uppercase"
                    data-testid="budget-banner"
                >
                    <span className="flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4" />
                        Universal Key balance low — Claude calls are failing. Top up at Profile → Universal Key → Add Balance.
                    </span>
                    <button
                        onClick={() => setBudgetWarn("")}
                        className="underline hover:no-underline"
                        data-testid="budget-banner-dismiss"
                    >
                        dismiss
                    </button>
                </div>
            )}
            {/* Iter 31.212 — demo mode banner. Renders only when role===demo. */}
            <DemoBanner />
            {/* Letterhead */}
            <header
                className="relative border-b border-ink/25 bg-paper-light/70 backdrop-blur-sm sticky top-0 z-40"
                data-testid="app-header"
                data-compact={headerCompact ? "1" : "0"}
            >
                <div className="absolute inset-0 pointer-events-none grain opacity-70" />
                {headerCompact ? (
                    /* Iter 31.197 — slim 40px bar for TV / big-screen browsers. */
                    <div
                        className="max-w-[1600px] mx-auto px-4 py-1.5 relative flex items-center justify-between gap-3"
                        data-testid="header-compact-bar"
                    >
                        <Link
                            to="/"
                            className="flex items-center gap-2 group"
                            data-testid="brand-home-link-compact"
                        >
                            <span
                                className="inline-flex items-center justify-center w-6 h-6 border border-ink/40"
                                aria-hidden
                            >
                                <svg
                                    width="16" height="16" viewBox="0 0 32 32"
                                    fill="none" stroke="currentColor" strokeWidth="1.6"
                                    strokeLinecap="round" strokeLinejoin="round"
                                    className="text-ink"
                                >
                                    <path d="M3 13 Q16 17 29 13" />
                                    <path d="M8 13 Q9 6 16 6 Q23 6 24 13" />
                                    <path d="M6 22 Q16 28 26 22 Q16 18 6 22 Z" />
                                    <circle cx="16" cy="22" r="2.5" fill="currentColor" />
                                </svg>
                            </span>
                            <span className="font-heading text-sm text-ink tracking-[0.18em] uppercase">
                                Spy Finance
                            </span>
                            <span className="text-stamp-red text-xs" aria-hidden>●</span>
                        </Link>
                        <div className="flex items-center gap-2">
                            <RadioPlayer />
                            {user && (
                                <>
                                    <AvatarSlot user={user} />
                                    <button
                                        onClick={() => setNavOpen((v) => !v)}
                                        aria-label={navOpen ? "Close menu" : "Open menu"}
                                        data-testid="desktop-nav-toggle-compact"
                                        className="inline-flex items-center gap-1 border border-ink/30 px-2 py-1 font-heading uppercase tracking-[0.18em] text-[10px] text-ink hover:border-stamp-red hover:text-stamp-red"
                                    >
                                        {navOpen ? <X className="w-3 h-3" /> : <Menu className="w-3 h-3" />}
                                        Menu
                                    </button>
                                    <button
                                        onClick={toggleHeaderCompact}
                                        title="Expand header"
                                        aria-label="Expand header"
                                        data-testid="header-expand-button"
                                        className="text-ink-soft hover:text-stamp-red border border-ink/30 p-1"
                                    >
                                        <Maximize2 className="w-3 h-3" />
                                    </button>
                                    <button
                                        onClick={() => { logout(); nav("/login"); }}
                                        className="text-ink-soft hover:text-stamp-red"
                                        data-testid="logout-button-compact"
                                        title="Sign off"
                                        aria-label="Sign off"
                                    >
                                        <LogOut className="w-4 h-4" strokeWidth={1.5} />
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                ) : (
                <div className="max-w-[1600px] mx-auto px-6 pt-4 pb-0 relative">
                    <div className="flex items-start justify-between">
                        <Link
                            to="/"
                            className="flex items-baseline gap-4 group"
                            data-testid="brand-home-link"
                        >
                            {/* Fedora-and-eye mark. Inline SVG so it picks up the
                                current ink colour and scales cleanly on retina. */}
                            <span
                                className="hidden md:inline-flex items-center justify-center w-12 h-12 border border-ink/40 sf-glow"
                                aria-hidden
                            >
                                <svg
                                    width="32" height="32" viewBox="0 0 32 32"
                                    fill="none" stroke="currentColor" strokeWidth="1.6"
                                    strokeLinecap="round" strokeLinejoin="round"
                                    className="text-ink"
                                >
                                    {/* fedora brim */}
                                    <path d="M3 13 Q16 17 29 13" />
                                    {/* fedora crown */}
                                    <path d="M8 13 Q9 6 16 6 Q23 6 24 13" />
                                    <path d="M10 10 Q16 8 22 10" opacity="0.5" />
                                    {/* eye */}
                                    <path d="M6 22 Q16 28 26 22 Q16 18 6 22 Z" />
                                    <circle cx="16" cy="22" r="2.5" fill="currentColor" />
                                </svg>
                            </span>
                            <div className="leading-none">
                                <div className="font-heading text-2xl md:text-5xl text-ink tracking-[0.06em] uppercase flex items-baseline gap-3">
                                    Spy Finance
                                    <span className="text-stamp-red text-base md:text-xl" aria-hidden>●</span>
                                </div>
                                <div className="hidden md:block font-body text-[11px] tracking-[0.25em] text-ink-soft uppercase mt-1">
                                    Build Engine · Agent Three · Twenty-year horizon from day one
                                </div>
                                <div
                                    className="font-body text-[10px] md:text-[11px] tracking-[0.22em] text-stamp-red uppercase mt-1 flex items-center gap-1.5"
                                    data-testid="brand-domain"
                                >
                                    <span className="inline-block w-1 h-1 bg-stamp-red rounded-full" aria-hidden />
                                    Spyfinance.cloud · Bridge of Continuity from Garys-Shed.Cloud
                                </div>
                            </div>
                        </Link>

                        <div className="flex items-center gap-2 md:gap-4">
                            <div className="hidden md:block text-right">
                                <div className="label-typed">File Ref</div>
                                <div className="font-heading text-sm text-ink tracking-[0.22em]">BJLIAM / 06</div>
                            </div>
                            {user && (
                                <div className="flex items-center gap-2 md:gap-3 md:border-l md:border-ink/25 md:pl-4">
                                    {/* Iter 31.76 — Avatar: Google picture if present, Prisoner-numbered badge fallback */}
                                    <AvatarSlot user={user} />
                                    <div className="hidden md:block text-right leading-tight">
                                        <div className="label-typed">Subject</div>
                                        <div
                                            className="font-heading text-sm text-ink tracking-wider"
                                            data-testid="current-user-email"
                                        >
                                            {user.name && user.name !== "No. 6" ? user.name : "No. 6"}
                                        </div>
                                    </div>
                                    <span
                                        className="hidden md:inline-flex stamp-red border-2 border-stamp-red px-2 py-0.5 text-[10px]"
                                        data-testid="current-user-role"
                                    >
                                        {user.role === "internal" ? "CONTROL" : "FIELD"}
                                    </span>
                                    {credits && (
                                        <Link
                                            to="/billing"
                                            data-testid="credits-pill"
                                            title={`${credits.balance} credits · 1 credit = 1 Brief · 3 = 1 Cold Read · top up on /billing`}
                                            className={`hidden md:inline-flex items-center gap-1 border px-2 py-0.5 text-[10px] uppercase tracking-[0.18em] font-heading transition-colors ${
                                                credits.balance < 5
                                                    ? "border-stamp-red text-stamp-red hover:bg-stamp-red hover:text-paper"
                                                    : "border-ink/40 text-ink hover:border-stamp-red hover:text-stamp-red"
                                            }`}
                                        >
                                            <span className="text-[9px] opacity-70">¢</span>
                                            {credits.balance}
                                        </Link>
                                    )}
                                    {tier && (
                                        <Link
                                            to="/billing"
                                            data-testid="tier-badge"
                                            title={tier.tier === "free" ? "Free tier — view plans" : `${tier.tier} · ${tier.remaining_days} days remaining`}
                                            className={`hidden md:inline-flex items-center gap-1 border px-2 py-0.5 text-[10px] uppercase tracking-[0.18em] font-heading transition-colors ${
                                                tier.tier === "free"
                                                    ? "border-ink/30 text-ink-soft hover:border-stamp-red hover:text-stamp-red"
                                                    : "border-stamp-green text-stamp-green hover:bg-stamp-green hover:text-paper"
                                            }`}
                                        >
                                            {tier.tier !== "free" && <BadgeCheck className="w-3 h-3" />}
                                            {tier.tier}
                                            {tier.tier !== "free" && tier.remaining_days != null && (
                                                <span className="text-[9px] opacity-80">· {tier.remaining_days}d</span>
                                            )}
                                        </Link>
                                    )}
                                    <button
                                        onClick={() => { logout(); nav("/login"); }}
                                        className="inline-flex items-center gap-1 md:gap-2 text-ink-soft hover:text-stamp-red font-body text-xs uppercase tracking-[0.18em] md:tracking-[0.2em]"
                                        data-testid="logout-button"
                                        title="Be seeing you"
                                        aria-label="Sign off"
                                    >
                                        <LogOut className="w-4 h-4" strokeWidth={1.5} />
                                        <span className="hidden sm:inline">Sign off</span>
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Folder tabs — desktop only (md+). Primary daily-use surfaces only.
                        Full grouped list lives in the hamburger drawer (iter 31.7). */}
                    <nav className="mt-5 hidden md:flex flex-wrap items-end gap-1" aria-label="Primary">
                        {primaryNavItems.map((n) => {
                            const active = loc.pathname === n.to || loc.pathname.startsWith(n.to + "/");
                            return (
                                <Link
                                    key={n.to}
                                    to={n.to}
                                    className={`folder-tab ${active ? "active" : ""}`}
                                    data-testid={`nav-${n.to.replace("/", "")}-link`}
                                >
                                    <span>{n.label}</span>
                                </Link>
                            );
                        })}
                        {/* Desktop hamburger — opens the same drawer as mobile for quick A→Z jump.
                            Radio nub sits next to it per No. 6 (21 May 2026). */}
                        <div className="ml-2 self-end mb-[2px] flex items-center gap-2">
                            <RadioPlayer />
                            <button
                                onClick={() => setNavOpen((v) => !v)}
                                aria-label={navOpen ? "Close menu" : "Open menu"}
                                data-testid="desktop-nav-toggle"
                                className="inline-flex items-center gap-2 border border-ink/30 px-3 py-1.5 font-heading uppercase tracking-[0.22em] text-[10px] text-ink hover:border-stamp-red hover:text-stamp-red"
                            >
                                {navOpen ? <X className="w-3 h-3" /> : <Menu className="w-3 h-3" />}
                                {navOpen ? "Close" : "Menu"}
                            </button>
                        </div>
                    </nav>

                    {/* Mobile nav — compact bar with current section + hamburger toggle. */}
                    <div className="md:hidden mt-4 flex items-center justify-between border-t border-dashed border-ink/20 pt-3" data-testid="mobile-nav-bar">
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => setNavOpen((v) => !v)}
                                aria-label={navOpen ? "Close menu" : "Open menu"}
                                data-testid="mobile-nav-toggle"
                                className="flex items-center gap-2 border border-ink/30 px-3 py-1.5 font-heading uppercase tracking-[0.22em] text-[10px] text-ink hover:border-stamp-red hover:text-stamp-red"
                            >
                                {navOpen ? <X className="w-3 h-3" /> : <Menu className="w-3 h-3" />}
                                {navOpen ? "Close" : "Menu"}
                            </button>
                            <RadioPlayer />
                        </div>
                        <div className="font-heading uppercase tracking-[0.18em] text-sm text-stamp-red">
                            {activeItem.label}
                        </div>
                    </div>
                </div>
                )}
                {/* Iter 31.197 — Shrink-header toggle, anchored top-right when full. */}
                {!headerCompact && (
                    <button
                        onClick={toggleHeaderCompact}
                        title="Compact header (TV / big-screen mode)"
                        aria-label="Compact header"
                        data-testid="header-compact-button"
                        className="absolute top-2 right-2 text-ink-soft hover:text-stamp-red border border-ink/30 p-1 z-10"
                    >
                        <Minimize2 className="w-3 h-3" />
                    </button>
                )}
                {/* tab underline seam */}
                <div className="absolute bottom-0 left-0 right-0 h-[1px] bg-ink/25" />

                {/* Grouped drawer — themed sections so 21 items don't dump as a flat
                    A→Z dump. Same drawer used by mobile + desktop hamburger. iter 31.7. */}
                {navOpen && (
                    <div
                        className="border-t border-ink/25 bg-paper-light/95 backdrop-blur-sm absolute left-0 right-0 z-50"
                        data-testid="mobile-nav-drawer"
                    >
                        <nav className="px-6 py-4 space-y-4 max-h-[80vh] overflow-y-auto" aria-label="Primary drawer">
                            {drawerSections.map((section) => (
                                <div
                                    key={section.title}
                                    data-testid={`nav-section-${section.title.toLowerCase()}`}
                                >
                                    <div className="label-typed text-[10px] tracking-[0.25em] text-stamp-red mb-2">
                                        {section.title}
                                    </div>
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                                        {section.items.map((n) => {
                                            const active = loc.pathname === n.to || loc.pathname.startsWith(n.to + "/");
                                            // Iter 31.321 — pending badge on Handoffs entry
                                            const badge = (n.to === "/handoffs" && pendingHandoffs > 0)
                                                ? pendingHandoffs
                                                : null;
                                            return (
                                                <Link
                                                    key={n.to}
                                                    to={n.to}
                                                    data-testid={`mobile-nav-${n.to.replace("/", "")}-link`}
                                                    className={`flex items-center justify-between border px-3 py-2 font-heading uppercase tracking-[0.18em] text-[11px] ${
                                                        active
                                                            ? "border-stamp-red text-stamp-red bg-stamp-red/5"
                                                            : "border-ink/25 text-ink hover:border-stamp-red"
                                                    }`}
                                                >
                                                    <span>{n.label}</span>
                                                    {badge != null ? (
                                                        <span
                                                            data-testid="nav-handoffs-badge"
                                                            className="ml-2 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 bg-stamp-red text-paper-light font-mono text-[10px]"
                                                        >{badge}</span>
                                                    ) : null}
                                                </Link>
                                            );
                                        })}
                                    </div>
                                </div>
                            ))}
                        </nav>
                    </div>
                )}
            </header>

            <main className="max-w-[1600px] mx-auto px-6 py-10 relative">
                {/* Ambient coffee rings, corner-of-desk */}
                <div className="coffee-ring" style={{ top: "40vh", right: "-40px" }} aria-hidden />
                <div className="coffee-ring" style={{ top: "82vh", left: "-30px", width: 100, height: 100, opacity: 0.35 }} aria-hidden />
                {children}
            </main>

            <footer
                className="border-t border-ink/25 mt-16 py-5 font-body tracking-[0.2em] uppercase text-[10px] text-ink-soft"
                data-testid="app-footer"
            >
                <div className="max-w-[1600px] mx-auto px-6 flex flex-wrap items-center justify-between gap-3">
                    <span>Build Engine · Agent Three · Property of No. 6 · Be seeing you</span>
                    <span className="flex items-center gap-4">
                        <span>© Spy Finance · A twenty-year build</span>
                        <span className="stamp-red text-[11px]">BJLIAM</span>
                    </span>
                </div>
            </footer>
        </div>
    );
}
